import * as Slider from '@radix-ui/react-slider'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { KestrelModal } from 'features/kestrelViewer/ui/tools/KestrelModal'
import React, { useState } from 'react'
import styled from 'styled-components'
import { KVGridColors, KVInspectionGrid } from 'types'

const ControlGroup = styled.div`
  margin-bottom: 0.75rem;
  text-align: left;
`

const ControlLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  margin-bottom: 0.25rem;
  font-weight: 500;
`

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const StyledSliderRoot = styled(Slider.Root)`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  touch-action: none;
  height: 20px;
  flex: 1;
`

const StyledSliderTrack = styled(Slider.Track)`
  background-color: rgba(255, 255, 255, 0.15);
  position: relative;
  border-radius: 9999px;
  height: 4px;
  flex: 1;
`

const StyledSliderRange = styled(Slider.Range)`
  position: absolute;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 9999px;
  height: 100%;
`

const StyledSliderThumb = styled(Slider.Thumb)`
  display: block;
  width: 12px;
  height: 20px;
  background-color: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.95);
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
`

const SliderValue = styled.div`
  min-width: 2.5rem;
  font-size: 0.875rem;
  text-align: right;
  color: rgba(255, 255, 255, 0.9);
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const UpdateButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  flex: 1;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:active {
    background: rgba(255, 255, 255, 0.35);
    transform: translateY(1px);
  }
`

const StyledDropdownContainer = styled.div`
  .rs-picker-default .rs-picker-toggle.rs-btn {
    background-color: rgba(255, 255, 255, 0.15) !important;
    border-color: rgba(255, 255, 255, 0.2) !important;
    color: white !important;
    border-radius: 0.25rem !important;
  }

  .rs-picker-toggle-value {
    color: white !important;
    font-weight: 500 !important;
  }

  .rs-picker-toggle-placeholder {
    color: rgba(255, 255, 255, 0.6) !important;
  }

  .rs-btn:hover {
    background-color: rgba(255, 255, 255, 0.25) !important;
    border-color: rgba(255, 255, 255, 0.4) !important;
  }

  .rs-btn:focus {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3) !important;
  }
`

export const InspectionGridSettings: React.FC = () => {
  const {
    sendCommand,
    inspectionGridSettingsOpen,
    setInspectionGridSettingsOpen,
    kestrelModel,
    setInspectionGridSettings,
    inspectionGridSettings,
  } = useKestrelViewerContext()

  const [localSpacing, setLocalSpacing] = useState(
    inspectionGridSettings?.gridSpacing
  )
  const [localOffset, setLocalOffset] = useState(
    inspectionGridSettings?.gridOffset
  )
  const [localUnit, setLocalUnit] = useState<'meters' | 'feet' | undefined>(
    inspectionGridSettings?.unit
  )
  const [localColor, setLocalColor] = useState<KVGridColors | undefined>(
    inspectionGridSettings?.color
  )
  const [localLineThickness, setLocalLineThickness] = useState(
    inspectionGridSettings?.lineThickness
  )
  const [localLabelVisible, setLocalLabelVisible] = useState<
    'visible' | 'hidden'
  >(inspectionGridSettings?.labelVisibility ? 'visible' : 'hidden')

  if (!inspectionGridSettingsOpen) {
    return null
  }

  const handleClose = () => {
    setInspectionGridSettingsOpen(false)
  }

  const handleUpdate = () => {
    if (kestrelModel && localSpacing && localOffset && localLineThickness) {
      const measurementScale = kestrelModel.data.measurementScale / 100
      const meterToFeet = 1 / 3.28084
      const factor =
        localUnit === 'meters'
          ? measurementScale
          : measurementScale / meterToFeet

      const data = {
        gridSpacing: localSpacing / factor,
        gridOffset: localOffset / factor,
        unit: localUnit,
        color: localColor,
        lineThickness: localLineThickness / factor,
        labelVisibility: localLabelVisible === 'visible',
      }

      setInspectionGridSettings(data as KVInspectionGrid)
      sendCommand({
        command: KestrelCommands.UPDATE_INSPECTION_GRID,
        ...data,
      })
    }

    handleClose()
  }

  return (
    <KestrelModal handleClose={handleClose} title='Inspection Grid Settings'>
      <ControlGroup>
        <ControlLabel>Grid Spacing</ControlLabel>
        <SliderContainer>
          <StyledSliderRoot
            min={0.5}
            max={100.0}
            step={0.5}
            value={[localSpacing ?? 0.5]}
            onValueChange={(value) => setLocalSpacing(value[0])}>
            <StyledSliderTrack>
              <StyledSliderRange />
            </StyledSliderTrack>
            <StyledSliderThumb />
          </StyledSliderRoot>
          <SliderValue>{localSpacing?.toFixed(1)}</SliderValue>
        </SliderContainer>
      </ControlGroup>

      <ControlGroup>
        <ControlLabel>Grid Offset from Roof</ControlLabel>
        <SliderContainer>
          <StyledSliderRoot
            min={0.5}
            max={10.0}
            step={0.5}
            value={[localOffset ?? 0.5]}
            onValueChange={(value) => setLocalOffset(value[0])}>
            <StyledSliderTrack>
              <StyledSliderRange />
            </StyledSliderTrack>
            <StyledSliderThumb />
          </StyledSliderRoot>
          <SliderValue>{localOffset?.toFixed(1)}</SliderValue>
        </SliderContainer>
      </ControlGroup>

      <ControlGroup>
        <ControlLabel>Unit</ControlLabel>
        <StyledDropdownContainer>
          <Dropdown
            options={[
              {
                value: 'meters',
                label: 'meters',
              },
              {
                value: 'feet',
                label: 'feet',
              },
            ]}
            onValueChange={(value: string) => {
              if (value === 'meters' || value === 'feet') {
                setLocalUnit(value)
              }
            }}
            defaultValue={localUnit}
            placeholder='Select unit'
            $backgroundColor='rgba(255, 255, 255, 0.1)'
            $borderColor='rgba(255, 255, 255, 0.2)'
            searchable={false}
            cleanable={false}
          />
        </StyledDropdownContainer>
      </ControlGroup>

      <ControlGroup>
        <ControlLabel>Line Thickness</ControlLabel>
        <SliderContainer>
          <StyledSliderRoot
            min={0.5}
            max={100.0}
            step={0.5}
            value={[localLineThickness ?? 0.5]}
            onValueChange={(value) => setLocalLineThickness(value[0])}>
            <StyledSliderTrack>
              <StyledSliderRange />
            </StyledSliderTrack>
            <StyledSliderThumb />
          </StyledSliderRoot>
          <SliderValue>{localLineThickness?.toFixed(1)}</SliderValue>
        </SliderContainer>
      </ControlGroup>

      <ControlGroup>
        <ControlLabel>Color</ControlLabel>
        <StyledDropdownContainer>
          <Dropdown
            options={[
              {
                value: KVGridColors.Red,
                label: 'red',
              },
              {
                value: KVGridColors.Blue,
                label: 'blue',
              },
              {
                value: KVGridColors.Green,
                label: 'green',
              },
              {
                value: KVGridColors.Gray,
                label: 'gray',
              },
              {
                value: KVGridColors.White,
                label: 'white',
              },
              {
                value: KVGridColors.Yellow,
                label: 'yellow',
              },
            ]}
            onValueChange={(value: string) => {
              setLocalColor(value as KVGridColors)
            }}
            defaultValue={localColor}
            placeholder='Select color'
            $backgroundColor='rgba(255, 255, 255, 0.1)'
            $borderColor='rgba(255, 255, 255, 0.2)'
            searchable={false}
            cleanable={false}
          />
        </StyledDropdownContainer>
      </ControlGroup>

      <ControlGroup>
        <ControlLabel>Label Visibility</ControlLabel>
        <StyledDropdownContainer>
          <Dropdown
            options={[
              {
                value: 'visible',
                label: 'visible',
              },
              {
                value: 'hidden',
                label: 'hidden',
              },
            ]}
            onValueChange={(value: string) => {
              if (value === 'visible' || value === 'hidden') {
                setLocalLabelVisible(value)
              }
            }}
            defaultValue={localLabelVisible}
            placeholder='Select label visibility'
            $backgroundColor='rgba(255, 255, 255, 0.1)'
            $borderColor='rgba(255, 255, 255, 0.2)'
            searchable={false}
            cleanable={false}
          />
        </StyledDropdownContainer>
      </ControlGroup>

      <ButtonContainer>
        <UpdateButton onClick={handleUpdate}>Update</UpdateButton>
      </ButtonContainer>
    </KestrelModal>
  )
}
